import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { DigcompCompetenceSelector } from '@/components/lms/digcomp/DigcompCompetenceSelector';
import { DigcompAIMapButton } from '@/components/lms/digcomp/DigcompAIMapButton';
import { useDigcompCompetences, useDigcompAreas, useDigcompLevels } from '@/hooks/useDigcompData';
import pentagonAllColors from '@/assets/digcomp/pentagon-all-colors.png';
import type { DigcompEntityType, DigcompMappingContent } from '@/hooks/useDigcompAIMapping';

export interface DigcompCompetenceRowsScope {
  courseId: string;
  moduleId?: string | null;
  lessonId?: string | null;
}

export interface DigcompCompetenceRowsProps {
  scope: DigcompCompetenceRowsScope;
  brandId: string;
  entityType: DigcompEntityType;
  content: DigcompMappingContent;
  description?: string;
  queryKeysToInvalidate?: string[];
}

export function DigcompCompetenceRows({
  scope,
  brandId,
  entityType,
  content,
  description = 'Link this content to digital competences from the DigComp 3.0 framework. Multiple competences with weights are supported.',
  queryKeysToInvalidate = ['digcomp-linked-content'],
}: DigcompCompetenceRowsProps) {
  const queryClient = useQueryClient();
  const { data: areas = [] } = useDigcompAreas(brandId);
  const { data: competences = [] } = useDigcompCompetences(brandId);
  const { data: levels = [] } = useDigcompLevels(brandId);

  const queryKey = ['course-competences', scope.courseId, scope.moduleId ?? 'course', scope.lessonId ?? 'course'];

  const { data: rows = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      let q = supabase
        .from('os_lms_course_competences')
        .select(`
          id,
          digcomp_competence_id,
          digcomp_level_id,
          weight,
          competence:os_lms_digcomp_competences(code, name, area_id)
        `)
        .eq('course_id', scope.courseId)
        .eq('brand_id', brandId);

      if (scope.moduleId) {
        q = q.eq('module_id', scope.moduleId).is('lesson_id', null);
      } else if (scope.lessonId) {
        q = q.eq('lesson_id', scope.lessonId).is('module_id', null);
      } else {
        q = q.is('module_id', null).is('lesson_id', null);
      }

      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
    enabled: !!scope.courseId && !!brandId,
  });

  const createMutation = useMutation({
    mutationFn: async ({
      competenceId,
      targetLevelId,
      weight = 1,
    }: {
      competenceId: string;
      targetLevelId?: string | null;
      weight?: number;
    }) => {
      const insertPayload: Record<string, unknown> = {
        brand_id: brandId,
        course_id: scope.courseId,
        digcomp_competence_id: competenceId,
        digcomp_level_id: targetLevelId ?? null,
        weight: Math.min(10, Math.max(1, weight || 1)),
      };
      if (scope.moduleId) {
        insertPayload.module_id = scope.moduleId;
        insertPayload.lesson_id = null;
      } else if (scope.lessonId) {
        insertPayload.lesson_id = scope.lessonId;
        insertPayload.module_id = null;
      } else {
        insertPayload.module_id = null;
        insertPayload.lesson_id = null;
      }

      const { error } = await supabase
        .from('os_lms_course_competences')
        .insert(insertPayload as any);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryKeysToInvalidate.forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
      toast.success('Competence added');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateWeightMutation = useMutation({
    mutationFn: async ({ id, weight }: { id: string; weight: number }) => {
      const { error } = await supabase
        .from('os_lms_course_competences')
        .update({ weight: Math.min(10, Math.max(1, weight)) })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryKeysToInvalidate.forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('os_lms_course_competences').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryKeysToInvalidate.forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
      toast.success('Competence removed');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [newCompetenceId, setNewCompetenceId] = useState<string | null>(null);
  const [newAreaId, setNewAreaId] = useState<string | null>(null);
  const [newLevelId, setNewLevelId] = useState<string | null>(null);
  const [newWeight, setNewWeight] = useState<number>(1);

  const handleAddCompetence = () => {
    if (!newCompetenceId) {
      toast.error('Select a competence first');
      return;
    }
    createMutation.mutate({
      competenceId: newCompetenceId,
      targetLevelId: newLevelId,
      weight: newWeight,
    });
    setNewCompetenceId(null);
    setNewAreaId(null);
    setNewLevelId(null);
    setNewWeight(1);
  };

  const handleAISuccess = async (result: any) => {
    if (result && 'competences' in result && result.competences.length > 0) {
      const existingIds = new Set(rows.map((r: any) => r.digcomp_competence_id));
      for (const c of result.competences) {
        const compId = c.competence_id || c.competenceId;
        if (compId && !existingIds.has(compId)) {
          try {
            await createMutation.mutateAsync({
              competenceId: compId,
              targetLevelId: c.target_level_id ?? c.targetLevelId ?? null,
              weight: c.weight ?? 1,
            });
            existingIds.add(compId);
          } catch {
            // Ignore duplicate or other errors
          }
        }
      }
      queryClient.invalidateQueries({ queryKey });
    }
  };

  return (
    <div className="p-4 border rounded-lg bg-gradient-to-br from-primary/5 to-primary/10 space-y-6">
      <div className="flex items-center gap-2">
        <img src={pentagonAllColors} alt="DigComp" className="h-5 w-5" />
        <h3 className="text-lg font-semibold">DigComp 3.0 Competences</h3>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>

      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <DigcompAIMapButton
            entityType={entityType}
            entityId={scope.lessonId ?? scope.moduleId ?? scope.courseId}
            brandId={brandId}
            content={content}
            onSuccess={handleAISuccess}
          />
        </div>

        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : rows.length > 0 ? (
          <div className="space-y-2">
            <Label>Linked Competences</Label>
            <div className="space-y-1">
              {rows.map((row: any) => {
                const comp = competences.find((c) => c.id === row.digcomp_competence_id);
                const area = comp ? areas.find((a) => a.id === comp.area_id) : null;
                const level = levels.find((l) => l.id === row.digcomp_level_id);
                return (
                  <div
                    key={row.id}
                    className="flex items-center justify-between gap-2 p-2 border rounded-lg bg-muted/30"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {area && (
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: area.color }}
                        />
                      )}
                      <span className="text-sm font-medium truncate">
                        {comp?.code} - {comp?.name}
                      </span>
                      {level && (
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          ({level.name})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="flex items-center gap-1">
                        <Label className="text-xs text-muted-foreground whitespace-nowrap">Weight</Label>
                        <Input
                          type="number"
                          min={1}
                          max={10}
                          className="w-14 h-8 text-sm"
                          defaultValue={row.weight ?? 1}
                          onBlur={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v) && v >= 1 && v <= 10) {
                              updateWeightMutation.mutate({ id: row.id, weight: v });
                            }
                          }}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => deleteMutation.mutate(row.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No competences linked yet.</p>
        )}

        <div className="border-t pt-4">
          <Label className="mb-2 block">Add Competence Manually</Label>
          <DigcompCompetenceSelector
            brandId={brandId}
            competenceId={newCompetenceId}
            areaId={newAreaId}
            levelId={newLevelId}
            onCompetenceChange={(competenceId, areaId) => {
              setNewCompetenceId(competenceId);
              setNewAreaId(areaId);
            }}
            onLevelChange={setNewLevelId}
            showLevelSelector
          />
          <div className="flex items-center gap-2 mt-2">
            <div className="flex items-center gap-1">
              <Label className="text-xs">Weight</Label>
              <Input
                type="number"
                min={1}
                max={10}
                className="w-14 h-8"
                value={newWeight}
                onChange={(e) => setNewWeight(Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 1)))}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddCompetence}
              disabled={!newCompetenceId || createMutation.isPending}
            >
              Add Competence
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
